/**
 * Portfolio Store
 * Centralized state management for portfolio view hierarchy,
 * filters, and multi-portfolio selection.
 * TASK-263: Multi-portfolio per client support
 * TASK-371: Removed legacy data/loading/error state (now handled by React Query)
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  ViewLevel,
  ViewContext,
  MyBookFilters,
  ClientFilters,
  PortfolioFilters,
  getDefaultMyBookFilters,
  getDefaultClientFilters,
  getDefaultPortfolioFilters,
  getDefaultViewContext,
} from '../../../types/portfolio-views';
import { Portfolio } from '../../api/portfolioApiService';

/**
 * Portfolio store state interface
 */
interface PortfolioState {
  // Portfolio selection
  selectedPortfolio: string;  // 'all' or clientId

  // TASK-263: Multi-portfolio support
  selectedPortfolioId: string | null;  // Actual portfolio UUID
  clientPortfolios: Portfolio[];  // List of portfolios for selected client
  portfoliosLoading: boolean;

  // Settings
  timeRange: string;

  // View Hierarchy State
  viewContext: ViewContext;
  myBookFilters: MyBookFilters;
  clientFilters: ClientFilters;
  portfolioFilters: PortfolioFilters;

  // Actions
  setSelectedPortfolio: (portfolio: string) => void;
  setTimeRange: (range: string) => void;
  reset: () => void;

  // TASK-263: Multi-portfolio actions
  setSelectedPortfolioId: (portfolioId: string | null) => void;
  setClientPortfolios: (portfolios: Portfolio[]) => void;
  setPortfoliosLoading: (loading: boolean) => void;
  selectPortfolio: (portfolioId: string | null) => void;  // Convenience method

  // View Hierarchy Actions
  setViewContext: (context: Partial<ViewContext>) => void;
  setMyBookFilters: (filters: Partial<MyBookFilters>) => void;
  setClientFilters: (filters: Partial<ClientFilters>) => void;
  setPortfolioFilters: (filters: Partial<PortfolioFilters>) => void;
  drillDown: (targetLevel: ViewLevel, id: string, clientId?: string) => void;
  drillUp: () => void;
  resetFilters: (level?: ViewLevel) => void;
}

/**
 * Initial state
 */
const initialState = {
  selectedPortfolio: 'all',

  // TASK-263: Multi-portfolio state
  selectedPortfolioId: null,
  clientPortfolios: [] as Portfolio[],
  portfoliosLoading: false,

  timeRange: '30d',

  // View hierarchy state
  viewContext: getDefaultViewContext(),
  myBookFilters: getDefaultMyBookFilters(),
  clientFilters: getDefaultClientFilters(),
  portfolioFilters: getDefaultPortfolioFilters(),
};

/**
 * Portfolio store implementation
 */
export const usePortfolioStore = create<PortfolioState>()(
  devtools(
    persist(
      (set, _get) => ({
        ...initialState,

        setSelectedPortfolio: (portfolio) => set({
          selectedPortfolio: portfolio,
        }, false, 'setSelectedPortfolio'),

        setTimeRange: (range) => set({ timeRange: range }, false, 'setTimeRange'),

        // Reset
        reset: () => set(initialState, false, 'reset'),

        // TASK-263: Multi-portfolio actions
        setSelectedPortfolioId: (portfolioId) => set({
          selectedPortfolioId: portfolioId,
        }, false, 'setSelectedPortfolioId'),

        setClientPortfolios: (portfolios) => set({
          clientPortfolios: portfolios
        }, false, 'setClientPortfolios'),

        setPortfoliosLoading: (loading) => set({
          portfoliosLoading: loading
        }, false, 'setPortfoliosLoading'),

        /**
         * Convenience method to select a portfolio
         * TASK-263: Multi-portfolio support
         */
        selectPortfolio: (portfolioId) => set((state) => {
          // If null is passed, select the default portfolio or first portfolio
          const newPortfolioId = portfolioId ||
            state.clientPortfolios.find(p => p.is_default)?.id ||
            state.clientPortfolios[0]?.id ||
            null;

          return {
            selectedPortfolioId: newPortfolioId,
            // Update view context if we're in portfolio view
            viewContext: state.viewContext.level === 'portfolio'
              ? { ...state.viewContext, portfolioId: newPortfolioId || undefined }
              : state.viewContext
          };
        }, false, 'selectPortfolio'),

        // View Hierarchy Management
        setViewContext: (context) => set((state) => ({
          viewContext: { ...state.viewContext, ...context }
        }), false, 'setViewContext'),

        setMyBookFilters: (filters) => set((state) => ({
          myBookFilters: { ...state.myBookFilters, ...filters }
        }), false, 'setMyBookFilters'),

        setClientFilters: (filters) => set((state) => ({
          clientFilters: { ...state.clientFilters, ...filters }
        }), false, 'setClientFilters'),

        setPortfolioFilters: (filters) => set((state) => ({
          portfolioFilters: { ...state.portfolioFilters, ...filters }
        }), false, 'setPortfolioFilters'),

        /**
         * Navigate down in the hierarchy with filter inheritance
         * My Book → Client: Client gets default portfolio filters
         * Client → Portfolio: Portfolio inherits client filters (minus comparePortfolios)
         *
         * TASK-210: Updated to accept optional clientId for portfolio navigation
         */
        drillDown: (targetLevel, id, clientId) => set((state) => {
          const currentLevel = state.viewContext.level;

          // Determine inherited filters based on navigation
          let inheritedFilters: Partial<PortfolioFilters | ClientFilters> = {};

          if (currentLevel === 'my-book' && targetLevel === 'client') {
            // Client inherits default portfolio filters
            inheritedFilters = getDefaultPortfolioFilters();
          } else if (currentLevel === 'client' && targetLevel === 'portfolio') {
            // Portfolio inherits client's portfolio filters (without comparePortfolios)
            const { comparePortfolios: _comparePortfolios, ...portfolioFilters } = state.clientFilters;
            inheritedFilters = portfolioFilters;
          }

          // Build new view context
          const newContext: ViewContext = {
            level: targetLevel,
            previousLevel: currentLevel,
          };

          // TASK-210: Properly handle clientId and portfolioId based on target level
          if (targetLevel === 'client') {
            newContext.clientId = id; // id is the clientId
            newContext.portfolioId = undefined; // Clear portfolio when going to client level
          }

          if (targetLevel === 'portfolio') {
            // Use provided clientId, or preserve existing, or extract from state
            newContext.clientId = clientId || state.viewContext.clientId;
            newContext.portfolioId = id; // id is the portfolioId
          }

          // Update state based on target level
          if (targetLevel === 'client') {
            return {
              viewContext: newContext,
              clientFilters: { ...state.clientFilters, ...inheritedFilters },
            };
          } else if (targetLevel === 'portfolio') {
            return {
              viewContext: newContext,
              portfolioFilters: { ...state.portfolioFilters, ...inheritedFilters },
            };
          }

          return { viewContext: newContext };
        }, false, 'drillDown'),

        /**
         * Navigate up in the hierarchy
         * Restores previous view level
         */
        drillUp: () => set((state) => {
          const currentLevel = state.viewContext.level;
          const previousLevel = state.viewContext.previousLevel;

          // Cannot drill up from top level
          if (currentLevel === 'my-book' || !previousLevel) {
            return state;
          }

          // Build new context based on previous level
          const newContext: ViewContext = {
            level: previousLevel,
            // When going back to client, keep 'my-book' as previousLevel
            // When going back to my-book, clear previousLevel
            previousLevel: previousLevel === 'my-book' ? undefined : 'my-book',
          };

          if (previousLevel === 'client') {
            newContext.clientId = state.viewContext.clientId;
          }

          return { viewContext: newContext };
        }, false, 'drillUp'),

        /**
         * Reset filters for specific level or all levels
         */
        resetFilters: (level) => set((state) => {
          if (!level) {
            // Reset all filters
            return {
              myBookFilters: getDefaultMyBookFilters(),
              clientFilters: getDefaultClientFilters(),
              portfolioFilters: getDefaultPortfolioFilters(),
            };
          }

          // Reset specific level filters
          if (level === 'my-book') {
            return { myBookFilters: getDefaultMyBookFilters() };
          } else if (level === 'client') {
            return { clientFilters: getDefaultClientFilters() };
          } else if (level === 'portfolio') {
            return { portfolioFilters: getDefaultPortfolioFilters() };
          }

          return state;
        }, false, 'resetFilters'),
      }),
      {
        name: 'portfolio-store',
        // `viewContext` is no longer persisted. It holds entity IDs, not preferences, and
        // localStorage outlives both the entity and the logged-in account: a stale clientId
        // made bare `/portfolio` redirect to a client that no longer existed, and
        // ClientLevelView wrote the bad id straight back on mount, so it never expired.
        // Nav destinations come from the URL now; the store follows the URL, never leads it.
        //
        // Stripped in `merge` rather than via version+migrate because zustand only migrates
        // when the stored blob carries a numeric `version` (middleware.js:408) — and the
        // entries that actually hold a stale viewContext were written before any version
        // existed, so migrate would skip precisely the browsers that need it. `merge` always
        // runs.
        merge: (persisted, current) => {
          const { viewContext: _dropped, ...rest } = (persisted ?? {}) as Record<string, unknown>;
          return { ...current, ...rest };
        },
        // Only persist user preferences, not data
        partialize: (state) => ({
          selectedPortfolio: state.selectedPortfolio,
          // TASK-263: Persist portfolio selection
          selectedPortfolioId: state.selectedPortfolioId,
          timeRange: state.timeRange,
          myBookFilters: state.myBookFilters,
          clientFilters: state.clientFilters,
          portfolioFilters: state.portfolioFilters,
        }),
      }
    ),
    { name: 'PortfolioStore' }
  )
);

/**
 * Selector hooks for specific state slices
 */
export const useSelectedPortfolio = () => usePortfolioStore((state) => state.selectedPortfolio);
export const useTimeRange = () => usePortfolioStore((state) => state.timeRange);

/**
 * View Hierarchy Selectors
 */

// View context selectors
export const useViewContext = () => usePortfolioStore((state) => state.viewContext);
export const useViewLevel = () => usePortfolioStore((state) => state.viewContext.level);

// View level checkers
export const useIsMyBookView = () => usePortfolioStore((state) => state.viewContext.level === 'my-book');
export const useIsClientView = () => usePortfolioStore((state) => state.viewContext.level === 'client');
export const useIsPortfolioView = () => usePortfolioStore((state) => state.viewContext.level === 'portfolio');

// Filter selectors
export const useMyBookFilters = () => usePortfolioStore((state) => state.myBookFilters);
export const useClientFilters = () => usePortfolioStore((state) => state.clientFilters);
export const usePortfolioFilters = () => usePortfolioStore((state) => state.portfolioFilters);

/**
 * Navigation actions hook
 * Provides navigation methods for drilling down/up in view hierarchy
 */
export const useNavigationActions = () => {
  const store = usePortfolioStore();
  return {
    drillDown: store.drillDown,
    drillUp: store.drillUp,
    setViewContext: store.setViewContext,
  };
};

/**
 * Filter actions hook
 * Provides filter management methods for all view levels
 */
export const useFilterActions = () => {
  const store = usePortfolioStore();
  return {
    setMyBookFilters: store.setMyBookFilters,
    setClientFilters: store.setClientFilters,
    setPortfolioFilters: store.setPortfolioFilters,
    resetFilters: store.resetFilters,
  };
};

/**
 * TASK-263: Multi-portfolio selectors
 */
export const useSelectedPortfolioId = () => usePortfolioStore((state) => state.selectedPortfolioId);
export const useClientPortfolios = () => usePortfolioStore((state) => state.clientPortfolios);
export const usePortfoliosLoading = () => usePortfolioStore((state) => state.portfoliosLoading);

/**
 * Get the currently selected portfolio object
 * TASK-263: Multi-portfolio support
 */
export const useSelectedPortfolioObject = () => usePortfolioStore((state) => {
  if (!state.selectedPortfolioId) return null;
  return state.clientPortfolios.find(p => p.id === state.selectedPortfolioId) || null;
});

/**
 * Check if viewing aggregated (all portfolios) or specific portfolio
 * TASK-263: Multi-portfolio support
 */
export const useIsAggregatedView = () => usePortfolioStore((state) => !state.selectedPortfolioId);

/**
 * TASK-263: Multi-portfolio actions hook
 */
export const usePortfolioSelectionActions = () => {
  const store = usePortfolioStore();
  return {
    setSelectedPortfolioId: store.setSelectedPortfolioId,
    setClientPortfolios: store.setClientPortfolios,
    setPortfoliosLoading: store.setPortfoliosLoading,
    selectPortfolio: store.selectPortfolio,
  };
};
